/**
 * Blog Topic Hub
 * -----------------------------------------------------------------------------
 * Client-side faceted filtering over server-rendered article cards.
 *
 * Every article is already in the HTML (good for crawlers); this only toggles
 * visibility, reorders, and keeps the URL in sync so a filtered view is
 * shareable and back/forward works.
 *
 * Facet state lives in the URL as ?topic=a,b&city=c&format=d using the short
 * value after the tag prefix (topic:terrarium -> terrarium).
 */
(function () {
  'use strict';

  var FACETS = ['topic', 'city', 'format'];

  function shortValue(tag) {
    var i = tag.indexOf(':');
    return i === -1 ? tag : tag.slice(i + 1);
  }

  function init(root) {
    if (!root || root.dataset.hubReady === 'true') return;
    root.dataset.hubReady = 'true';

    var perPage = parseInt(root.dataset.perPage, 10) || 12;

    var grid = root.querySelector('[data-hub-grid]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-hub-card]'));
    var inputs = Array.prototype.slice.call(root.querySelectorAll('[data-hub-facet]'));
    var pillars = Array.prototype.slice.call(root.querySelectorAll('[data-hub-pillar]'));
    var chipsWrap = root.querySelector('[data-hub-chips]');
    var clearButtons = Array.prototype.slice.call(root.querySelectorAll('[data-hub-clear]'));
    var sortTrigger = root.querySelector('[data-hub-sort-trigger]');
    var sortMenu = root.querySelector('[data-hub-sort-menu]');
    var sortValue = root.querySelector('[data-hub-sort-value]');
    var sortOptions = Array.prototype.slice.call(root.querySelectorAll('[data-hub-sort-option]'));
    var visibleEl = root.querySelector('[data-hub-visible]');
    var wordEl = root.querySelector('[data-hub-result-word]');
    var emptyEl = root.querySelector('[data-hub-empty]');
    var moreWrap = root.querySelector('[data-hub-more-wrap]');
    var moreBtn = root.querySelector('.js-blog-hub-more');
    var langButtons = Array.prototype.slice.call(root.querySelectorAll('[data-hub-lang]'));
    var railToggle = root.querySelector('[data-hub-rail-toggle]');
    var rail = root.querySelector('[data-hub-rail]');
    var activeCountEl = root.querySelector('[data-hub-active-count]');

    if (!grid || !cards.length) return;

    // `lang` is a single-select dimension, not a multi-select facet. It defaults
    // to the first language button (English), so other-language articles never
    // appear in the grid unless the reader asks for them.
    var defaultLang = langButtons.length ? langButtons[0].dataset.hubLang : 'all';

    var state = {
      topic: [],
      city: [],
      format: [],
      lang: defaultLang,
      sort: 'new',
      shown: perPage
    };
    var labels = {};

    inputs.forEach(function (input) {
      var label = input.parentNode.querySelector('.blog-hub__opt-label');
      labels[input.value] = label ? label.textContent.trim() : input.value;
    });

    /* ----------------------------------------------------------------- data */

    function cardTags(card, facet) {
      var raw = (card.dataset[facet] || '').trim();
      return raw ? raw.split(/\s+/) : [];
    }

    function matchesLang(card) {
      if (state.lang === 'all') return true;
      return (card.dataset.lang || '') === state.lang;
    }

    /**
     * Does the card pass every facet except `ignore`? Used for facet counts.
     * Language is applied on top of this in every case — it scopes the whole
     * page, so facet counts should reflect the chosen language.
     */
    function matches(card, ignore) {
      if (!matchesLang(card)) return false;
      for (var i = 0; i < FACETS.length; i++) {
        var facet = FACETS[i];
        if (facet === ignore) continue;
        var selected = state[facet];
        if (!selected.length) continue;
        var tags = cardTags(card, facet);
        var hit = selected.some(function (value) {
          return tags.indexOf(value) !== -1;
        });
        if (!hit) return false;
      }
      return true;
    }

    /* ------------------------------------------------------------------- URL */

    function readUrl() {
      var params = new URLSearchParams(window.location.search);
      FACETS.forEach(function (facet) {
        var raw = params.get(facet);
        if (!raw) return;
        var wanted = raw.split(',').map(function (v) {
          return facet + ':' + v.trim();
        });
        state[facet] = wanted.filter(function (tag) {
          return Object.prototype.hasOwnProperty.call(labels, tag);
        });
      });
      var lang = params.get('lang');
      if (lang) {
        var wantedLang = 'lang:' + lang.trim();
        var known = langButtons.some(function (btn) {
          return btn.dataset.hubLang === wantedLang;
        });
        if (known) state.lang = wantedLang;
      }

      var sort = params.get('sort');
      if (sort && ['new', 'old', 'az'].indexOf(sort) !== -1) state.sort = sort;
    }

    function writeUrl() {
      var params = new URLSearchParams(window.location.search);
      FACETS.concat(['lang', 'sort']).forEach(function (key) {
        params.delete(key);
      });

      // Build by hand so multi-value facets stay readable (?topic=a,b rather
      // than ?topic=a%2Cb) — these URLs get shared and indexed.
      var pairs = [];
      if (state.lang !== defaultLang) pairs.push('lang=' + shortValue(state.lang));
      FACETS.forEach(function (facet) {
        if (state[facet].length) {
          pairs.push(facet + '=' + state[facet].map(shortValue).join(','));
        }
      });
      if (state.sort !== 'new') pairs.push('sort=' + state.sort);

      var carried = params.toString();
      if (carried) pairs.unshift(carried);

      var query = pairs.join('&');
      var url = window.location.pathname + (query ? '?' + query : '');
      window.history.replaceState({}, '', url);
    }

    /* ----------------------------------------------------------------- render */

    function syncInputs() {
      inputs.forEach(function (input) {
        input.checked = state[input.dataset.hubFacet].indexOf(input.value) !== -1;
      });
      pillars.forEach(function (pillar) {
        var on = state.topic.length === 1 && state.topic[0] === pillar.dataset.hubPillar;
        pillar.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      langButtons.forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.dataset.hubLang === state.lang ? 'true' : 'false');
      });
    }

    function renderCounts() {
      inputs.forEach(function (input) {
        var facet = input.dataset.hubFacet;
        var count = cards.filter(function (card) {
          return cardTags(card, facet).indexOf(input.value) !== -1 && matches(card, facet);
        }).length;

        var countEl = input.parentNode.querySelector('.blog-hub__opt-count');
        if (countEl) countEl.textContent = count;
        input.parentNode.classList.toggle('is-zero', count === 0 && !input.checked);
      });

      pillars.forEach(function (pillar) {
        var tag = pillar.dataset.hubPillar;
        var total = cards.filter(function (card) {
          return cardTags(card, 'topic').indexOf(tag) !== -1 && matchesLang(card);
        }).length;
        var countEl = pillar.querySelector('[data-hub-pillar-count]');
        if (countEl) countEl.textContent = total + (total === 1 ? ' article' : ' articles');
        pillar.hidden = total === 0;
      });

      var languagesWithArticles = 0;
      langButtons.forEach(function (btn) {
        var value = btn.dataset.hubLang;
        var total =
          value === 'all'
            ? cards.length
            : cards.filter(function (card) {
                return (card.dataset.lang || '') === value;
              }).length;
        var countEl = btn.querySelector('[data-hub-lang-count]');
        if (countEl) countEl.textContent = total;
        btn.dataset.empty = total === 0 ? 'true' : 'false';
        if (total > 0) languagesWithArticles += 1;
      });

      // Nothing to switch between if only one language has anything published.
      var langGroup = langButtons.length ? langButtons[0].parentNode : null;
      if (langGroup) {
        langGroup.dataset.single = languagesWithArticles < 2 ? 'true' : 'false';
      }
    }

    function renderChips() {
      if (!chipsWrap) return;
      chipsWrap.innerHTML = '';
      var active = [];
      FACETS.forEach(function (facet) {
        state[facet].forEach(function (tag) {
          active.push({ facet: facet, tag: tag });
        });
      });

      active.forEach(function (item) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'collection-grid-pill blog-hub__chip';
        chip.innerHTML =
          '<span>' + (labels[item.tag] || shortValue(item.tag)) + '</span><b aria-hidden="true">×</b>';
        chip.setAttribute('aria-label', 'Remove filter ' + (labels[item.tag] || item.tag));
        chip.addEventListener('click', function () {
          state[item.facet] = state[item.facet].filter(function (t) {
            return t !== item.tag;
          });
          state.shown = perPage;
          update();
        });
        chipsWrap.appendChild(chip);
      });

      clearButtons.forEach(function (btn) {
        if (btn.classList.contains('blog-hub__clear--inline')) return;
        btn.hidden = active.length === 0;
      });

      if (activeCountEl) {
        activeCountEl.textContent = active.length;
        activeCountEl.hidden = active.length === 0;
      }
    }

    function sortCards(list) {
      var sorted = list.slice();
      if (state.sort === 'new') {
        sorted.sort(function (a, b) {
          return Number(b.dataset.date) - Number(a.dataset.date);
        });
      } else if (state.sort === 'old') {
        sorted.sort(function (a, b) {
          return Number(a.dataset.date) - Number(b.dataset.date);
        });
      } else if (state.sort === 'az') {
        sorted.sort(function (a, b) {
          return (a.dataset.title || '').localeCompare(b.dataset.title || '');
        });
      }
      return sorted;
    }

    function update(skipUrl) {
      var hits = cards.filter(function (card) {
        return matches(card, null);
      });

      var ordered = sortCards(hits);
      var fragment = document.createDocumentFragment();
      ordered.forEach(function (card) {
        fragment.appendChild(card);
      });
      grid.appendChild(fragment);

      cards.forEach(function (card) {
        card.hidden = true;
      });
      ordered.slice(0, state.shown).forEach(function (card) {
        card.hidden = false;
      });

      if (visibleEl) visibleEl.textContent = hits.length;
      if (wordEl) wordEl.textContent = hits.length === 1 ? 'article' : 'articles';
      if (emptyEl) emptyEl.hidden = hits.length !== 0;
      if (moreWrap) moreWrap.hidden = hits.length <= state.shown;

      if (moreBtn) {
        var remaining = hits.length - state.shown;
        var labelEl = moreBtn.querySelector('.jx-btn-label, .ab-cta-label, span');
        if (labelEl && remaining > 0) {
          labelEl.textContent = 'Show ' + Math.min(perPage, remaining) + ' more of ' + hits.length;
        }
      }

      syncInputs();
      renderCounts();
      renderChips();
      if (!skipUrl) writeUrl();
    }

    /* ---------------------------------------------------------------- events */

    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var facet = input.dataset.hubFacet;
        if (input.checked) {
          if (state[facet].indexOf(input.value) === -1) state[facet].push(input.value);
        } else {
          state[facet] = state[facet].filter(function (t) {
            return t !== input.value;
          });
        }
        state.shown = perPage;
        update();
      });
    });

    pillars.forEach(function (pillar) {
      pillar.addEventListener('click', function () {
        var tag = pillar.dataset.hubPillar;
        var alreadyOnly = state.topic.length === 1 && state.topic[0] === tag;
        state.topic = alreadyOnly ? [] : [tag];
        state.shown = perPage;
        update();
        var body = root.querySelector('.blog-hub__body');
        if (body && typeof body.scrollIntoView === 'function') {
          body.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });

    clearButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        FACETS.forEach(function (facet) {
          state[facet] = [];
        });
        state.shown = perPage;
        update();
      });
    });

    langButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var value = btn.dataset.hubLang;
        if (state.lang === value) return;
        state.lang = value;

        // Drop any facet selections that have no articles in the new language,
        // otherwise switching can land the reader on an empty grid.
        FACETS.forEach(function (facet) {
          state[facet] = state[facet].filter(function (tag) {
            return cards.some(function (card) {
              return cardTags(card, facet).indexOf(tag) !== -1 && matchesLang(card);
            });
          });
        });

        state.shown = perPage;
        update();
      });
    });

    /* ------------------------------------------------- custom sort dropdown */

    function syncSort() {
      sortOptions.forEach(function (option) {
        var on = option.dataset.hubSortOption === state.sort;
        option.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on && sortValue) sortValue.textContent = option.textContent.trim();
      });
    }

    function openSort() {
      if (!sortMenu || !sortTrigger) return;
      sortMenu.hidden = false;
      sortTrigger.setAttribute('aria-expanded', 'true');
      var active =
        sortOptions.filter(function (o) {
          return o.getAttribute('aria-selected') === 'true';
        })[0] || sortOptions[0];
      if (active) focusOption(active);
    }

    function closeSort(refocus) {
      if (!sortMenu || !sortTrigger) return;
      sortMenu.hidden = true;
      sortTrigger.setAttribute('aria-expanded', 'false');
      sortOptions.forEach(function (o) {
        o.classList.remove('is-active');
      });
      if (refocus) sortTrigger.focus();
    }

    function focusOption(option) {
      sortOptions.forEach(function (o) {
        o.classList.toggle('is-active', o === option);
      });
      option.focus();
    }

    function chooseSort(value) {
      state.sort = value;
      syncSort();
      update();
      closeSort(true);
    }

    if (sortTrigger && sortMenu) {
      sortTrigger.addEventListener('click', function () {
        if (sortMenu.hidden) {
          openSort();
        } else {
          closeSort(false);
        }
      });

      sortTrigger.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openSort();
        }
      });

      sortOptions.forEach(function (option) {
        option.addEventListener('click', function () {
          chooseSort(option.dataset.hubSortOption);
        });

        option.addEventListener('mouseenter', function () {
          sortOptions.forEach(function (o) {
            o.classList.toggle('is-active', o === option);
          });
        });

        option.addEventListener('keydown', function (event) {
          var index = sortOptions.indexOf(option);
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            focusOption(sortOptions[(index + 1) % sortOptions.length]);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            focusOption(sortOptions[(index - 1 + sortOptions.length) % sortOptions.length]);
          } else if (event.key === 'Home') {
            event.preventDefault();
            focusOption(sortOptions[0]);
          } else if (event.key === 'End') {
            event.preventDefault();
            focusOption(sortOptions[sortOptions.length - 1]);
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            chooseSort(option.dataset.hubSortOption);
          } else if (event.key === 'Escape' || event.key === 'Tab') {
            closeSort(event.key === 'Escape');
          }
        });
      });

      document.addEventListener('click', function (event) {
        if (sortMenu.hidden) return;
        if (!sortTrigger.parentNode.contains(event.target)) closeSort(false);
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !sortMenu.hidden) closeSort(true);
      });
    }

    if (moreBtn) {
      moreBtn.addEventListener('click', function (event) {
        event.preventDefault();
        state.shown += perPage;
        update();
      });
    }

    if (railToggle && rail) {
      railToggle.addEventListener('click', function () {
        var open = rail.classList.toggle('is-open');
        railToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    window.addEventListener('popstate', function () {
      FACETS.forEach(function (facet) {
        state[facet] = [];
      });
      state.lang = 'all';
      readUrl();
      state.shown = perPage;
      update(true);
    });

    readUrl();
    syncSort();
    update(true);
  }

  function boot() {
    document.querySelectorAll('[data-blog-hub]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-blog-hub]');
    if (root) init(root);
  });
})();
